export interface VizContainerColorProps {
  /**
   * Text color.
   */
  color?: VizContainerColor;
  /**
   * Background color.
   */
  backgroundColor?: VizContainerColor;
}

/**
 * Determines how a given color value is handled within a `VizContainer`.
 * - `'container'`: Use the default color value from `Container`. Will override color values set by `useVizPanelColor`.
 * - `string`: Explicit color value. Will override color values set by `useVizPanelColor` and `Container`. In most use
 *   cases, this should be a color value from design system `theme` from `chrono-ui`.
 *
 * By default (or when set to `undefined`), color values are set in the following priority order:
 * - Color from `useVizPanelColor`, when set.
 * - Default color value from `Container`.
 */
export type VizContainerColor = string;
const VIZ_CONTAINER_COLOR_FROM_CONTAINER = 'container';

/**
 * Utility function for normalizing the color values in VizContainer.
 */
export function normalizeVizContainerColor(
  vizContainerColor: VizContainerColor | undefined,
  vizPanelColor: string | undefined
) {
  // Return `undefined` when we want to use the color from `Container`. By setting the color to undefined, it will
  // fall back to using the styles from `Container`.
  const inheritVizContainerColor = undefined;

  if (!vizContainerColor) {
    // If no viz container color, fall back to viz panel color. If viz panel color is undefined, fall back to color
    // from `Container`.
    return vizPanelColor ?? inheritVizContainerColor;
  }

  if (vizContainerColor === VIZ_CONTAINER_COLOR_FROM_CONTAINER) {
    // Use color from container.
    return undefined;
  }

  // Container color is set and is not a special constant. Use it as the color.
  return vizContainerColor;
}
